/**
 * SEO.jsx — central SEO/AEO/GEO tag manager (react-helmet-async)
 *
 * Each public page imports this and passes the props for ITS page.
 * Site-wide defaults (Organization, WebSite, brand-level OG image) live in
 * /public/index.html and are NOT duplicated here — Helmet only manages
 * what changes per-route, so we don't fight the static defaults.
 *
 * Usage:
 *   <SEO
 *     title="..."
 *     description="..."
 *     path="/services"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Service", ... }}
 *   />
 */
import { Helmet } from "react-helmet-async";

const SITE = "https://fortunesourcings.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-default.jpg`;

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const url = `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
  const fullTitle = title?.includes("Fortune Sourcings")
    ? title
    : `${title} · Fortune Sourcings`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Fortune Sourcings" />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
